import InfoIcon from '@mui/icons-material/Info'
import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import Stack from '@mui/material/Stack'
import { useMutation, useQuery } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import KNCaption from 'components/KNCaption/KNCaption'
import KNClipboardField from 'components/KNClipboardField/KNClipboardField'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNForm from 'components/KNForm/KNForm'
import KNFormPhone from 'components/KNForm/KNFormPhone'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import KNFormText from 'components/KNForm/KNFormText'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { zonedDate } from 'global/helpers/dates'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { getSharingLanguages } from 'global/helpers/languages'
import { getRequestErrorMessages } from 'global/helpers/requestError'
import { regexEmail } from 'global/helpers/validators'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm, type ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { shareTokenViaEmail, shareTokenViaSms, shareTokenViaWhatsApp, tripShareTokenQueryOptions } from './TripDashboard.service'
import type {
  ShareTripViaEmailFormValues,
  ShareTripViaSmsFormValues,
  ShareTripViaWhatsAppFormValues,
  TripData,
} from './TripDashboard.types'

interface ShareDialogPayload {
  trip: TripData
  weblinkToken?: string
}

interface ShareDialogProps {
  payload: ShareDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const ShareDialog = ({ payload, open, onAction, onClose }: ShareDialogProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const {
    handleSubmit: handleSubmitEmail,
    setValue: setValueEmail,
    reset: resetEmail,
    control: controlEmail,
    formState: formStateEmail,
    setError: setErrorEmail,
  } = useForm<ShareTripViaEmailFormValues>()
  const {
    handleSubmit: handleSubmitSms,
    setValue: setValueSms,
    reset: resetSms,
    control: controlSms,
    formState: formStateSms,
    getValues: getValuesSms,
    setError: setErrorSms,
  } = useForm<ShareTripViaSmsFormValues>()
  const {
    handleSubmit: handleSubmitWhatsApp,
    setValue: setValueWhatsApp,
    reset: resetWhatsApp,
    control: controlWhatsApp,
    formState: formStateWhatsApp,
    getValues: getValuesWhatsApp,
    setError: setErrorWhatsApp,
  } = useForm<ShareTripViaWhatsAppFormValues>()

  const [sharingLanguage, setSharingLanguage] = useState('en')
  const [whatsAppConcurrentSession, setWhatsAppConcurrentSession] = useState(false)
  const shareTokenQuery = useQuery({
    ...tripShareTokenQueryOptions(payload.trip.entityId, payload.weblinkToken),
    enabled: open,
  })
  const shareToken = shareTokenQuery.isSuccess
    ? `${window.location.origin}/wl/${shareTokenQuery.data.weblinkToken}`
    : ''
  const shareTokenErrors = shareTokenQuery.isError
    ? { weblink: { message: t('errors.GenericError') } }
    : undefined
  const shareViaEmailMutation = useMutation({
    mutationFn: (data: ShareTripViaEmailFormValues) =>
      shareTokenViaEmail(payload.trip.entityId, { ...data, weblink: shareToken }, payload.weblinkToken),
    onError: (error) => setErrorEmail('root', processServerErrorMessages(error)),
  })
  const shareViaSmsMutation = useMutation({
    mutationFn: (data: ShareTripViaSmsFormValues) =>
      shareTokenViaSms(payload.trip.entityId, { ...data, weblink: shareToken }, payload.weblinkToken),
    onError: (error) => setErrorSms('root', processServerErrorMessages(error)),
  })
  const shareViaWhatsAppMutation = useMutation({
    mutationFn: (data: ShareTripViaWhatsAppFormValues) =>
      shareTokenViaWhatsApp(payload.trip.entityId, { ...data, weblink: shareToken }, payload.weblinkToken),
    onError: (error) => {
      if (getRequestErrorMessages(error).some(({ code }) => code === 'ConcurrentTripSession')) {
        setWhatsAppConcurrentSession(true)
        return
      }
      setErrorWhatsApp('root', processServerErrorMessages(error))
    },
  })

  useEffect(() => {
    if (open) {
      resetEmail(
        processDefaultValues({
          email: payload.trip.assignedDriver?.email,
          language: sharingLanguage,
        }),
      )
      resetSms(
        processDefaultValues({
          phoneNumber: payload.trip.assignedDriver?.contactNumber,
          language: sharingLanguage,
        }),
      )
      resetWhatsApp(
        processDefaultValues({
          mode: 'START_SESSION',
          phoneNumber: payload.trip.assignedDriver?.contactNumber,
          language: sharingLanguage,
        }),
      )
      setSharingLanguage(payload.trip.assignedDriver?.language ?? 'en')
      setWhatsAppConcurrentSession(false)
    }
  }, [open])

  useEffect(() => {
    setValueEmail('language', sharingLanguage)
    setValueSms('language', sharingLanguage)
    setValueWhatsApp('language', sharingLanguage)
  }, [sharingLanguage])

  const onSubmitEmail: SubmitHandler<ShareTripViaEmailFormValues> = async (data: ShareTripViaEmailFormValues) => {
    if (shareTokenQuery.isError) {
      return
    }

    await handleFormSubmission(
      () => shareViaEmailMutation.mutateAsync(data),
      {
        onSuccess: () => {
          onAction()
          analyticsEvent('polestar_cs_trip_shared', [getRouteName(location.pathname), 'email'])
        },
      },
    )
  }

  const onSubmitSms: SubmitHandler<ShareTripViaSmsFormValues> = async (data: ShareTripViaSmsFormValues) => {
    if (shareTokenQuery.isError) {
      return
    }

    await handleFormSubmission(
      () => shareViaSmsMutation.mutateAsync(data),
      {
        onSuccess: () => {
          onAction()
          analyticsEvent('polestar_cs_trip_shared', [getRouteName(location.pathname), 'sms'])
        },
      },
    )
  }

  const onSubmitWhatsApp: SubmitHandler<ShareTripViaWhatsAppFormValues> = async (data: ShareTripViaWhatsAppFormValues) => {
    if (shareTokenQuery.isError) {
      return
    }

    await handleFormSubmission(
      () => shareViaWhatsAppMutation.mutateAsync(data),
      {
        onSuccess: () => {
          onAction()
          analyticsEvent('polestar_cs_trip_shared', [getRouteName(location.pathname), 'whatsapp'])
        },
      },
    )
  }

  const onWhatsAppConcurrentSessionClose = () => {
    setWhatsAppConcurrentSession(false)
  }

  return (
    <>
      <KNDialog
        open={open && !whatsAppConcurrentSession}
        onClose={onClose}
        preventClosing={formStateEmail.isSubmitting || formStateSms.isSubmitting || formStateWhatsApp.isSubmitting}
        title={`${payload.trip.voyageNumber} — ${t('screens.cs.trip_dashboard.card.actions.share_trip')}`}
      >
        <KNDialogFormErrors errors={shareTokenErrors} />
        <KNDialogFormErrors errors={formStateEmail.errors?.root} />
        <KNDialogFormErrors errors={formStateSms.errors?.root} />
        <KNDialogFormErrors errors={formStateWhatsApp.errors?.root} />
        {(payload.trip.lastShareChannel && payload.trip.lastShareTimestamp) && (
          <KNCaption icon={<InfoIcon />} sx={{ marginBottom: 1 }}>
            {t(`screens.cs.trip_dashboard.share.last_shared_via.${payload.trip.lastShareChannel}`, {
              timestamp: zonedDate(payload.trip.lastShareTimestamp, 'full_no_year'),
            })}
          </KNCaption>
        )}
        <DialogContentText mb={2}>
          {t('screens.cs.trip_dashboard.share.message')}
        </DialogContentText>
        <Stack spacing={2}>
          <KNClipboardField
            value={shareToken}
            loading={shareTokenQuery.isFetching}
            onAction={(): void => {
              analyticsEvent('polestar_cs_trip_shared', [getRouteName(location.pathname), 'link'])
            }}
          />
          <KNAdvancedDropdown
            label={t('screens.cs.trip_dashboard.share.language')}
            onChange={(newValue): void => {
              setSharingLanguage(newValue)
            }}
            value={sharingLanguage}
            options={getSharingLanguages().map((language) => ({
              label: language.label,
              value: language.languageCode,
            }))}
            sx={{
              width: '50%',
            }}
          />
          <KNForm onSubmit={handleSubmitEmail(onSubmitEmail)}>
            <Stack
              spacing={{ xs: 0.5, sm: 1 }}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'start', sm: 'end' }}
            >
              <KNFormText
                name="email"
                label={t('screens.cs.trip_dashboard.share.via_email')}
                placeholder={t('screens.cs.trip_dashboard.share.email_placeholder')}
                control={controlEmail}
                disabled={!shareTokenQuery.isSuccess || formStateEmail.isSubmitting} // NOSONAR
                rules={{
                  pattern: {
                    value: regexEmail,
                    message: t('form.validation.invalid_email'),
                  },
                  required: t('form.validation.required'),
                }}
              />
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={!shareTokenQuery.isSuccess || formStateEmail.isSubmitting} // NOSONAR
                loading={formStateEmail.isSubmitting}
                onClick={handleSubmitEmail(onSubmitEmail)}
              >
                {t('form.send')}
              </Button>
            </Stack>
          </KNForm>
          <KNForm onSubmit={handleSubmitSms(onSubmitSms)}>
            <Stack
              spacing={{ xs: 0.5, sm: 1 }}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'start', sm: 'end' }}
            >
              <KNFormPhone
                name="phoneNumber"
                label={t('screens.cs.trip_dashboard.share.via_sms')}
                placeholder={t('screens.cs.trip_dashboard.share.phone_placeholder')}
                control={controlSms}
                disabled={!shareTokenQuery.isSuccess || formStateSms.isSubmitting} // NOSONAR
                rules={{
                  required: t('form.validation.required'),
                }}
                getValues={getValuesSms}
              />
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={!shareTokenQuery.isSuccess || formStateSms.isSubmitting} // NOSONAR
                loading={formStateSms.isSubmitting}
                onClick={handleSubmitSms(onSubmitSms)}
              >
                {t('form.send')}
              </Button>
            </Stack>
          </KNForm>
          <KNForm onSubmit={handleSubmitWhatsApp(onSubmitWhatsApp)}>
            <Stack
              spacing={{ xs: 0.5, sm: 1 }}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'start', sm: 'end' }}
            >
              <KNFormPhone
                name="phoneNumber"
                label={t('screens.cs.trip_dashboard.share.via_whatsapp')}
                placeholder={t('screens.cs.trip_dashboard.share.phone_placeholder')}
                control={controlWhatsApp}
                disabled={!shareTokenQuery.isSuccess || formStateWhatsApp.isSubmitting} // NOSONAR
                rules={{
                  required: t('form.validation.required'),
                }}
                getValues={getValuesWhatsApp}
              />
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={!shareTokenQuery.isSuccess || formStateWhatsApp.isSubmitting} // NOSONAR
                loading={formStateWhatsApp.isSubmitting}
                onClick={handleSubmitWhatsApp(onSubmitWhatsApp)}
              >
                {t('form.send')}
              </Button>
            </Stack>
          </KNForm>
        </Stack>
      </KNDialog>

      <KNDialog
        open={open && whatsAppConcurrentSession}
        onClose={onWhatsAppConcurrentSessionClose}
        closeLabel={t('general.back')}
        preventClosing={formStateWhatsApp.isSubmitting}
        title={`${payload.trip.voyageNumber} — ${t('screens.cs.trip_dashboard.card.actions.share_trip')}`}
        actions={
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={!shareTokenQuery.isSuccess || formStateWhatsApp.isSubmitting}
            loading={formStateWhatsApp.isSubmitting}
            onClick={handleSubmitWhatsApp(onSubmitWhatsApp)}
          >
            {t('form.send')}
          </Button>
        }
        onSubmit={handleSubmitWhatsApp(onSubmitWhatsApp)}
      >
        <KNDialogFormErrors errors={shareTokenErrors} />
        <KNDialogFormErrors errors={formStateWhatsApp.errors?.root} />
        <DialogContentText mb={2}>
          {t('screens.cs.trip_dashboard.share.whatsapp_concurrent_session.disclaimer')}
        </DialogContentText>
        <KNFormRadioGroup
          name="mode"
          control={controlWhatsApp}
          options={[
            {
              value: 'REPLACE_SESSION',
              label: t('screens.cs.trip_dashboard.share.whatsapp_concurrent_session.replace_session'),
            },
            {
              value: 'SEND_WEBLINK',
              label: t('screens.cs.trip_dashboard.share.whatsapp_concurrent_session.send_weblink'),
            },
          ]}
          rules={{
            required: t('form.validation.required'),
            validate: {
              invalidMode: (value): ValidateResult => {
                if (value === 'START_SESSION') {
                  return t('form.validation.required')
                }
                return true
              },
            },
          }}
          disabled={!shareTokenQuery.isSuccess || formStateWhatsApp.isSubmitting} // NOSONAR
        />
      </KNDialog>
    </>
  )
}

export default ShareDialog
